import React, { SetStateAction, useCallback, useState } from 'react';
import { RefillFormState } from '../model';
import {
  ContentContainer,
  FormScrollView,
  InputContainer,
  MainText,
  Row
} from 'src/module/FormKit/components';
import { Headline, TextInput } from 'src/components';
import { getIn } from 'formik';
import { useTranslation } from 'react-i18next';
import UploadFiles, { ImageUpload } from 'src/components/UploadFiles';
import { Margin } from 'src/constants';
import styled from 'styled-components/native';
import RichTextRenderer from 'src/components/PracticeTextRenderer';

const ProductInfoSection: React.FC<RefillFormState> = ({
  values: { additionalInfo },
  formTitle,
  headerText,
  setFieldValue,
  validateField,
  errors
}) => {
  const { t } = useTranslation('onlineBooking');

  const [images, setImages] = useState<Record<string, ImageUpload>>(additionalInfo.images ?? {});
  const onImageChange = useCallback(
    async (action: SetStateAction<Record<string, ImageUpload>>) => {
      setImages((prev) => {
        const value = typeof action === 'function' ? action(prev) : action;
        void setFieldValue('additionalInfo.images', value);
        setTimeout(async () => validateField('additionalInfo.images'), 100);
        return value;
      });
    },
    [setFieldValue, validateField]
  );

  return (
    <FormScrollView>
      <ContentContainer>
        <MainText as={Headline}>{formTitle}</MainText>
        <MainText as={Headline}>{t('productInfo.title')}</MainText>
        {headerText && <RichTextRenderer>{headerText}</RichTextRenderer>}
      </ContentContainer>
      <Row>
        <InputContainer halfSize>
          <TextInput
            placeholder={t('form.productDescription')}
            value={additionalInfo.productDescription}
            onChangeText={async (value) =>
              setFieldValue('additionalInfo.productDescription', value, false)
            }
            onBlur={async () => {
              await validateField('additionalInfo.productDescription');
            }}
            error={!!getIn(errors, 'additionalInfo.productDescription')}
            msg={t(getIn(errors, 'additionalInfo.productDescription'))}
          />
        </InputContainer>
        <InputContainer halfSize>
          <TextInput
            placeholder={t('form.productQuantity')}
            autoCapitalize='none'
            value={String(additionalInfo?.productQuantity).replace(/\D+/g, '')}
            onChangeText={async (value) => {
              const sanitizedText = value.replace(/\D+/g, '');
              const parsed = parseInt(sanitizedText);
              void setFieldValue(
                'additionalInfo.productQuantity',
                isNaN(parsed) ? undefined : parsed,
                false
              );
            }}
            onBlur={async () => {
              await validateField('additionalInfo.productQuantity');
            }}
            error={!!getIn(errors, 'additionalInfo.productQuantity')}
            msg={t(getIn(errors, 'additionalInfo.productQuantity'))}
          />
        </InputContainer>
      </Row>
      <ContentContainer>
        <MainText>{t('productInfo.description')}</MainText>
      </ContentContainer>
      <Row>
        <InputContainer>
          <TextInput
            multiline
            style={{ minHeight: 100 }}
            label={t('form.productComments')}
            value={additionalInfo?.productComments}
            onChangeText={async (text) =>
              setFieldValue('additionalInfo.productComments', text, false)
            }
            onBlur={async () => validateField('additionalInfo.productComments')}
            error={!!getIn(errors, 'additionalInfo.productComments')}
            msg={t(getIn(errors, 'additionalInfo.productComments'))}
          />
        </InputContainer>
      </Row>
      <StyledUpload images={images} onImageChange={onImageChange} buttonMode='outlined' />
    </FormScrollView>
  );
};

export default ProductInfoSection;

const StyledUpload = styled(UploadFiles)`
  flex-direction: column-reverse;
  gap: ${Margin.Large}px;
`;
